import { GridFilterModel } from '@mui/x-data-grid';
import { AdvancedFilterModel } from '@nohara-dxdev/react-core';
import { createContext, useContext, ReactNode, useRef } from 'react';

import { useDebounceValue } from '@/hooks';

// Sortable fields matching backend definition
export const itemTemplateSortableFields = [
  'name',
  'templateKey',
  'createdAt',
  'updatedAt',
] as const;

export type ItemTemplateSortableField = (typeof itemTemplateSortableFields)[number];
export type ItemTemplateSortOrder = 'asc' | 'desc';

/**
 * ItemTemplateFilterValues interface matching the backend API contract
 *
 * Filter fields (itemTypeIds, constructionTypeIds, projectRoleTags, isBoardItem,
 * canConnectToBoard, excludeIds) are now handled via the filterModel/advancedFilterModel using RSQL.
 *
 * Special params:
 * - nameOrTemplateKeyMatch: Full-text search on name and templateKey (handled separately)
 */
export interface ItemTemplateFilterValues {
  // Internal filter models (array of DataGrid filter models)
  // Each filterModel in the array is treated as an AND group
  // Multiple filterModels are combined with AND operator
  // This will be converted to RSQL string in the API hook
  // Fields like itemTypeIds, constructionTypeIds, projectRoleTags, isBoardItem, canConnectToBoard
  // should be added as filter items in these models
  filterModel?: GridFilterModel[];

  // Advanced filter model (supports AND/OR between conditions)
  // When provided, takes precedence over filterModel
  // Will be converted to GridFilterModel format for RSQL conversion
  advancedFilterModel?: AdvancedFilterModel;

  // Special query parameter: Full-text search on name and templateKey
  // This is handled separately from RSQL as a custom filter param
  nameOrTemplateKeyMatch?: string;

  // Sorting parameters
  sortField?: ItemTemplateSortableField;
  sortOrder?: ItemTemplateSortOrder;
}

const ItemTemplateFilterContext = createContext<ItemTemplateFilterValues | undefined>(undefined);

export interface ItemTemplateFilterProviderProps {
  children: ReactNode;
  filterValues: ItemTemplateFilterValues;
  debounceTime?: number;
}

export const ItemTemplateFilterValuesProvider = ({
  children,
  filterValues,
  debounceTime = 500,
}: ItemTemplateFilterProviderProps) => {
  const filterValuesRef = useRef<ItemTemplateFilterValues | undefined>(filterValues);

  if (JSON.stringify(filterValues) !== JSON.stringify(filterValuesRef.current)) {
    filterValuesRef.current = filterValues;
  }

  const debouncedFilterValues = useDebounceValue(filterValuesRef.current, debounceTime);

  return (
    <ItemTemplateFilterContext.Provider value={debouncedFilterValues}>
      {children}
    </ItemTemplateFilterContext.Provider>
  );
};

export const useItemTemplateFilterValues = (): ItemTemplateFilterValues => {
  return useContext(ItemTemplateFilterContext) ?? {};
};
